import { useMemo } from 'react';
import { useUser } from '../context/UserContext';
import { useProjectOptional } from '../context/ProjectContext';
import { POLICIES } from '../static/policies';

export const useAccess = () => {
  const orgContext = useUser();
  const projectContext = useProjectOptional();

  const orgRole = orgContext?.role;
  const orgPermSet = orgContext?.permissionSet;
  const orgIsReady = orgContext?.isReady ?? false;

  const projectRole = projectContext?.projectRole;
  const projectPermSet = projectContext?.permissionSet;
  const projectIsReady = projectContext?.isReady ?? true;
  const projectId = projectContext?.projectId;

  const isInsideProject = !!projectId;

  // Any org role that isn't the base 'org-member' tier has global access and
  // should never be downscoped to a single project's permission set.
  const isGlobalOrgRole = !!orgRole && orgRole !== 'org-member';

  // Use project context only when we're inside a project AND the user is a
  // plain org-member (i.e. their access is entirely project-scoped).
  const useProjectCtx = isInsideProject && !isGlobalOrgRole;

  const currentRole = useProjectCtx ? projectRole : orgRole;
  const activePermSet = useProjectCtx ? projectPermSet : orgPermSet;

  const isReady = orgIsReady && projectIsReady;

  return useMemo(() => {
    const isSuperAdmin = orgRole === 'super-admin';

    const canAccess = (policyName) => {
      if (!isReady) return false;

      if (isSuperAdmin) return true;

      const policy = POLICIES[policyName];
      if (!policy) {
        console.warn(`[Access Control] Policy "${policyName}" not found.`);
        return false;
      }

      const { permissions = [], fallbackRoles = [] } = policy;

      // STEP 1 — Permission-Based Access Control (PBAC)
      // Wildcard '*' grants all; otherwise ANY permission match is enough.
      if (permissions.length > 0) {
        if (activePermSet?.has('*') || permissions.some((p) => activePermSet?.has(p))) {
          return true;
        }
        return false;
      }

      // STEP 2 — Role-Based Access Control (RBAC) fallback
      if (fallbackRoles.length > 0 && currentRole) {
        return fallbackRoles.includes(currentRole);
      }

      return false;
    };

    return {
      canAccess,
      isReady,
      isInsideProject,
      activeRole: currentRole,
    };
  }, [
    orgRole,
    orgPermSet,
    projectRole,
    projectPermSet,
    isInsideProject,
    isReady,
    useProjectCtx,
    currentRole,
    activePermSet,
  ]);
};